import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {InboxModel} from '../../../models/api/inboxModel';
import {buildPublicId, tryParsePublicId} from '../../../models/applications/publicIdTypes';
import {SearchInboxCustomFieldDropdown} from '../dropdowns/search/searchCustomFieldInboxDropdown';
import {InlineEditingInputDisplay} from './inlineEditingInputDisplay';

/*
 * Constants.
 */

// Limit height so the dropdown is less likely to be repositioned. Corresponds to 4.5 items.
const inlineInboxDropdownHeight = 225;

/*
 * Types.
 */

interface InlineEditingInboxPopoverProps {
  onEditSave: HandlerOf<string>;
  onEditCancel: Handler;
  inboxPublicId: string;
  renderButtonContent: RendererOf<string>;
}

/*
 * Style.
 */

const StyledInlineEditingInputDisplay = styled(InlineEditingInputDisplay)`
  margin: 3px 0 2px 0;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  width: 100%;
`;

const StyledValueButtonDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(p) => p.theme.greys.shade20};
  color: ${(p) => p.theme.greys.shade90};
  border-radius: 4px;
  padding: 5px 8px;
`;

/*
 * Component.
 */

export const InlineEditingInboxPopover: FC<InlineEditingInboxPopoverProps> = (props) => {
  const theme = useUserTheme();
  const {onEditSave, onEditCancel, inboxPublicId, renderButtonContent} = props;

  const [popoverButtonRef, {width: popoverButtonWidth}] = useMeasureElement();

  const selectedInboxId = tryParsePublicId(inboxPublicId, 'inbox');

  const onChange = (inbox: InboxModel) => {
    onEditSave(buildPublicId('inbox', inbox.id));
  };

  return (
    <StyledInlineEditingInputDisplay>
      <StyledPopoverCoordinator
        renderButton={() => (
          <StyledValueButtonDiv ref={popoverButtonRef}>
            {renderButtonContent(inboxPublicId)}
            <Icon name="chevronDownSmall" color={theme.greys.shade70} />
          </StyledValueButtonDiv>
        )}
        renderModal={(popoverProps) => (
          <SearchInboxCustomFieldDropdown
            {...popoverProps}
            selectedInboxId={selectedInboxId}
            onChange={onChange}
            width={popoverButtonWidth}
            maxHeight={inlineInboxDropdownHeight}
          />
        )}
        onPopoverToggle={(isPopoverOpen) => {
          // User clicks out without selecting an option
          if (!isPopoverOpen) {
            onEditCancel();
          }
        }}
        isModalInitiallyOpen
      />
    </StyledInlineEditingInputDisplay>
  );
};
